import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { paymentsApi, contractsApi, clientsApi, vehiclesApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { DuplicateWeeksModal } from '../components/DuplicateWeeksModal';
import {
  Money,
  Plus,
  MagnifyingGlass,
  X,
  Check,
  Warning,
  CreditCard,
  Calendar,
  Trash,
  FileText,
  FilePdf,
  FileXls,
  DownloadSimple,
  Receipt,
  CaretDown,
  CaretUp,
  Eye,
  PencilSimple,
  ArrowsClockwise,
  CaretRight,
  Phone,
  Car
} from '@phosphor-icons/react';

const Payments = () => {
  const { canEdit, isAdmin } = useAuth();
  const [payments, setPayments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const todayStr = new Date().toISOString().substring(0, 10);
  const emptyForm = {
    contract_id: '',
    amount: '',
    payment_method: 'transferencia',
    payment_type: 'renta_normal',
    payment_date: todayStr,
    penalty_amount: '',
    accessory_amount: '',
    notes: ''
  };
  const [formData, setFormData] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [nextPending, setNextPending] = useState(null);
  const [pendingWeeks, setPendingWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null); // { id, week_number }
  const [weeklyStatus, setWeeklyStatus] = useState(null);
  // Inline editors dentro del modal Registrar Pago
  const [inlineEdit, setInlineEdit] = useState(null); // 'penalty' | 'accessory' | null
  const [inlinePenaltyValue, setInlinePenaltyValue] = useState('');
  const [inlineAccessoryValue, setInlineAccessoryValue] = useState('');
  const [inlineAccessoryNote, setInlineAccessoryNote] = useState('');
  const [inlineSaving, setInlineSaving] = useState(false);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterWeek, setFilterWeek] = useState('');
  const [editPayment, setEditPayment] = useState(null);
  // editPayment shape: { id, amount, penalty_amount, accessory_amount, days_late, notes, payment_method, payment_type, payment_date }
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementForm, setStatementForm] = useState({
    client_id: '',
    contract_id: '',
    period_type: 'weekly',
    start_date: '',
    end_date: '',
  });
  const [statementLoading, setStatementLoading] = useState(false);

  // Pending collection (Cobranza pendiente)
  const [pendingCollection, setPendingCollection] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingExpanded, setPendingExpanded] = useState(false);
  const [pendingViewMode, setPendingViewMode] = useState('contract'); // 'contract' | 'calendar'
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);

  // Weekly collection board (semana en curso agrupado por cliente)
  const [activeTab, setActiveTab] = useState('pagos'); // 'pagos' | 'cobranza-semanal'
  const [weeklyBoard, setWeeklyBoard] = useState(null);
  const [weeklyBoardLoading, setWeeklyBoardLoading] = useState(false);
  const [weeklyBoardSearch, setWeeklyBoardSearch] = useState('');

  // Editar mora / accesorios del schedule (cobranza pendiente)
  const [editPenaltyModal, setEditPenaltyModal] = useState(null);
  // shape: { schedule_id, client_name, current_penalty, new_penalty }
  const [editAccessoryModal, setEditAccessoryModal] = useState(null);
  // shape: { schedule_id, client_name, current_accessory, current_note, new_amount, new_note }
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [pendingModalSearch, setPendingModalSearch] = useState('');
  const [pendingExporting, setPendingExporting] = useState(false);

  const pendingFiltered = useMemo(() => {
    if (!pendingCollection) return [];
    const q = pendingModalSearch.trim().toLowerCase();
    if (!q) return pendingCollection.items;
    return pendingCollection.items.filter(
      (it) =>
        (it.client_name || '').toLowerCase().includes(q) ||
        (it.vehicle_plates || '').toLowerCase().includes(q) ||
        (it.vehicle_label || '').toLowerCase().includes(q)
    );
  }, [pendingCollection, pendingModalSearch]);

  const handleExportPendingFiltered = async () => {
    try {
      setPendingExporting(true);
      const ids = pendingFiltered.map((it) => it.schedule_id);
      const { data, headers } = await paymentsApi.exportPendingCollection(ids);
      const cd = headers['content-disposition'] || '';
      const match = cd.match(/filename=([^;]+)/);
      const filename = match ? match[1].trim() : `cobranza_pendiente_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setPendingExporting(false);
    }
  };

  useEffect(() => {
    loadData();
    loadPendingCollection();
  }, []);

  const loadPendingCollection = useCallback(async () => {
    try {
      setPendingLoading(true);
      const { data } = await paymentsApi.pendingCollection();
      setPendingCollection(data);
    } catch (err) {
      console.warn('[Payments] Could not load pending collection:', err);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const loadWeeklyBoard = useCallback(async () => {
    try {
      setWeeklyBoardLoading(true);
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/payments/weekly-collection`);
      setWeeklyBoard(data);
    } catch (err) {
      console.warn('[Payments] Could not load weekly-collection board:', err);
    } finally {
      setWeeklyBoardLoading(false);
    }
  }, []);

  const reloadWeeklyStatus = useCallback(async (contractId, weekNumber) => {
    if (!contractId) return;
    try {
      const { data } = await paymentsApi.weeklyStatus(contractId, weekNumber ?? selectedWeek?.week_number);
      if (data) {
        setWeeklyStatus(data);
        setFormData((prev) => ({
          ...prev,
          amount: data.total_pending > 0 ? data.total_pending : prev.amount,
        }));
      }
    } catch (err) {
      console.warn('[Payments] reload weekly-status:', err);
    }
  }, [selectedWeek]);

  const handleWeekChange = async (scheduleId) => {
    const week = pendingWeeks.find((s) => s.id === scheduleId) || null;
    setSelectedWeek(week);
    setNextPending(week);
    setInlineEdit(null);
    if (!week || !selectedContract) return;
    try {
      const { data } = await paymentsApi.weeklyStatus(selectedContract.id, week.week_number);
      setWeeklyStatus(data);
      setFormData((prev) => ({
        ...prev,
        schedule_id: week.id,
        amount: data.total_pending > 0 ? data.total_pending : (selectedContract.weekly_rent || ''),
      }));
    } catch (err) {
      console.warn('[Payments] weekly-status for week', week.week_number, err);
    }
  };

  const savePenaltyInline = async () => {
    if (!weeklyStatus?.schedule_id) return;
    const raw = String(inlinePenaltyValue).trim();
    if (raw === '' || Number.isNaN(Number(raw))) {
      toast.error('Ingresa un monto de mora válido');
      return;
    }
    try {
      setInlineSaving(true);
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/schedule/${weeklyStatus.schedule_id}/penalty`,
        { penalty_amount: Number(raw) },
      );
      toast.success('Mora actualizada');
      setInlineEdit(null);
      await reloadWeeklyStatus(selectedContract?.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al actualizar la mora');
    } finally {
      setInlineSaving(false);
    }
  };

  const condonePenaltyInline = async () => {
    if (!weeklyStatus?.schedule_id) return;
    if (!window.confirm('¿Condonar toda la mora de esta semana?')) return;
    try {
      setInlineSaving(true);
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/schedule/${weeklyStatus.schedule_id}/penalty`,
      );
      toast.success('Mora condonada');
      setInlineEdit(null);
      await reloadWeeklyStatus(selectedContract?.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al condonar la mora');
    } finally {
      setInlineSaving(false);
    }
  };

  const saveAccessoryInline = async () => {
    if (!weeklyStatus?.schedule_id) return;
    const raw = String(inlineAccessoryValue).trim();
    if (raw === '' || Number.isNaN(Number(raw))) {
      toast.error('Ingresa un monto de accesorio válido');
      return;
    }
    try {
      setInlineSaving(true);
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/schedule/${weeklyStatus.schedule_id}/accessory`,
        { accessory_amount: Number(raw), accessory_note: inlineAccessoryNote },
      );
      toast.success('Accesorio actualizado');
      setInlineEdit(null);
      await reloadWeeklyStatus(selectedContract?.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al actualizar accesorio');
    } finally {
      setInlineSaving(false);
    }
  };

  const removeAccessoryInline = async () => {
    if (!weeklyStatus?.schedule_id) return;
    if (!window.confirm('¿Quitar todos los accesorios pendientes de esta semana?')) return;
    try {
      setInlineSaving(true);
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/schedule/${weeklyStatus.schedule_id}/accessory`,
      );
      toast.success('Accesorio removido');
      setInlineEdit(null);
      await reloadWeeklyStatus(selectedContract?.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al quitar accesorio');
    } finally {
      setInlineSaving(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'cobranza-semanal' && !weeklyBoard && !weeklyBoardLoading) {
      loadWeeklyBoard();
    }
  }, [activeTab, weeklyBoard, weeklyBoardLoading, loadWeeklyBoard]);

  // ----- Edición mora/accesorios sobre el schedule (cobranza pendiente) -----
  const openEditPenalty = (item) => {
    setEditPenaltyModal({
      schedule_id: item.schedule_id,
      client_name: item.client_name,
      contract_week: item.contract_week,
      current_penalty: item.penalty_amount,
      new_penalty: item.penalty_amount.toString(),
    });
  };

  const submitEditPenalty = async () => {
    const m = editPenaltyModal;
    const value = parseFloat(m.new_penalty);
    if (Number.isNaN(value) || value < 0) {
      setError('Monto de mora inválido. Debe ser un número >= 0.');
      return;
    }
    try {
      setSavingSchedule(true);
      await paymentsApi.updateSchedulePenalty(m.schedule_id, value);
      setEditPenaltyModal(null);
      await loadPendingCollection();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleDeletePenalty = async (item) => {
    if (!window.confirm(
      `¿Condonar la mora de ${item.client_name} (Semana ${item.contract_week})?\n\n` +
      `Mora actual: ${formatCurrency(item.penalty_amount)}\n` +
      `Pasará a $0.00 y no se recalculará automáticamente.\n\n` +
      `Esta acción es reversible (puedes editar el monto después).`
    )) return;
    try {
      await paymentsApi.deleteSchedulePenalty(item.schedule_id);
      await loadPendingCollection();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const openEditAccessory = (item) => {
    setEditAccessoryModal({
      schedule_id: item.schedule_id,
      client_name: item.client_name,
      contract_week: item.contract_week,
      current_accessory: item.accessory_pending,
      current_note: item.accessory_note || '',
      new_amount: (item.accessory_pending || 0).toString(),
      new_note: item.accessory_note || '',
    });
  };

  const submitEditAccessory = async () => {
    const m = editAccessoryModal;
    const value = parseFloat(m.new_amount);
    if (Number.isNaN(value) || value < 0) {
      setError('Monto de accesorio inválido. Debe ser un número >= 0.');
      return;
    }
    try {
      setSavingSchedule(true);
      await paymentsApi.updateScheduleAccessory(m.schedule_id, value, m.new_note);
      setEditAccessoryModal(null);
      await loadPendingCollection();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleDeleteAccessory = async (item) => {
    if (!window.confirm(
      `¿Borrar los accesorios pendientes de ${item.client_name} (Semana ${item.contract_week})?\n\n` +
      `Accesorios actuales: ${formatCurrency(item.accessory_pending)}\n` +
      (item.accessory_note ? `Nota: "${item.accessory_note}"\n` : '') +
      `\nPasará a $0.00. Esta acción es reversible.`
    )) return;
    try {
      await paymentsApi.deleteScheduleAccessory(item.schedule_id);
      await loadPendingCollection();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [paymentsRes, contractsRes, clientsRes, vehiclesRes] = await Promise.all([
        paymentsApi.getAll(),
        contractsApi.getAll(),
        clientsApi.getAll(),
        vehiclesApi.getAll()
      ]);
      setPayments(paymentsRes.data);
      setContracts(contractsRes.data);
      setClients(clientsRes.data);
      setVehicles(vehiclesRes.data);
      // Refresh pending collection too — it may have changed after any CRUD on payments
      loadPendingCollection();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [loadPendingCollection]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    const data = {
      ...formData,
      amount: parseFloat(formData.amount)
    };
    if (selectedWeek?.id) data.schedule_id = selectedWeek.id;
    else delete data.schedule_id;
    // Send overrides only when user typed something
    if (formData.penalty_amount !== '' && formData.penalty_amount !== null) {
      data.penalty_amount = parseFloat(formData.penalty_amount);
    } else {
      delete data.penalty_amount;
    }
    if (formData.accessory_amount !== '' && formData.accessory_amount !== null) {
      data.accessory_amount = parseFloat(formData.accessory_amount);
    } else {
      delete data.accessory_amount;
    }

    try {
      const { data: created } = await paymentsApi.create(data);
      if (created?.merged) {
        toast.success(`Abono acumulado en el pago de la Semana ${created.week_number} (total ${formatCurrency(created.amount)})`);
      } else {
        toast.success(`Pago registrado en la Semana ${created?.week_number ?? ''}`);
      }
      setShowModal(false);
      setFormData(emptyForm);
      setSelectedContract(null);
      setNextPending(null);
      setPendingWeeks([]);
      setSelectedWeek(null);
      setWeeklyStatus(null);
      loadData();
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editPayment) return;
    try {
      setSaving(true);
      const payload = {
        amount: parseFloat(editPayment.amount),
        penalty_amount: parseFloat(editPayment.penalty_amount || 0),
        accessory_amount: parseFloat(editPayment.accessory_amount || 0),
        days_late: parseInt(editPayment.days_late || 0, 10),
        notes: editPayment.notes || ''
      };
      await paymentsApi.update(editPayment.id, payload);
      setEditPayment(null);
      loadData();
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleClearPenalty = async (paymentId) => {
    if (!window.confirm('¿Eliminar la penalización de este pago? Se pondrá en $0 y los días de atraso en 0.')) return;
    try {
      await paymentsApi.clearPenalty(paymentId);
      loadData();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleDeletePayment = async (payment) => {
    const clientName = getClientName(payment.client_id);
    if (!window.confirm(
      `¿Eliminar este pago de ${clientName} de la semana ${payment.week_number}?\n\n` +
      `Monto a revertir: ${formatCurrency(payment.amount)}\n\n` +
      `Esto:\n` +
      `• Sumará ${formatCurrency(payment.amount)} de vuelta al saldo del contrato\n` +
      `• Pondrá la semana ${payment.week_number} como pendiente/atrasada\n` +
      `• Eliminará la penalización asociada\n\n` +
      `Esta acción no se puede deshacer.`
    )) return;
    try {
      setDeletingPaymentId(payment.id);
      await paymentsApi.delete(payment.id);
      await loadData();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const openStatementModal = () => {
    // Pre-llenar fechas por defecto (últimos 90 días)
    const today = new Date();
    const past = new Date();
    past.setDate(today.getDate() - 90);
    setStatementForm({
      client_id: '',
      contract_id: '',
      period_type: 'weekly',
      start_date: past.toISOString().substring(0, 10),
      end_date: today.toISOString().substring(0, 10),
    });
    setShowStatementModal(true);
  };

  const handleDownloadStatement = async (format) => {
    if (!statementForm.client_id) {
      setError('Selecciona un cliente');
      return;
    }
    if (!statementForm.start_date || !statementForm.end_date) {
      setError('Selecciona fechas de inicio y fin');
      return;
    }
    try {
      setStatementLoading(true);
      setError('');
      const params = {
        period_type: statementForm.period_type,
        start_date: statementForm.start_date,
        end_date: statementForm.end_date,
      };
      if (statementForm.contract_id) params.contract_id = statementForm.contract_id;

      const url = format === 'pdf'
        ? paymentsApi.statementPdfUrl(statementForm.client_id, params)
        : paymentsApi.statementExcelUrl(statementForm.client_id, params);

      const res = await axios.get(url, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename=([^;]+)/);
      const filename = match ? match[1].trim() : `estado_cuenta.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setStatementLoading(false);
    }
  };

  const handleContractChange = async (contractId) => {
    const contract = contracts.find((c) => c.id === contractId);
    setSelectedContract(contract);
    setFormData({
      ...formData,
      contract_id: contractId,
      amount: contract?.weekly_rent || ''
    });
    setNextPending(null);
    setPendingWeeks([]);
    setSelectedWeek(null);
    setWeeklyStatus(null);
    if (contractId) {
      try {
        const [scheduleRes, weeklyRes] = await Promise.all([
          contractsApi.getSchedule(contractId),
          paymentsApi.weeklyStatus(contractId),
        ]);
        const pendingList = (scheduleRes.data || [])
          .filter((s) => s.status === 'pendiente' || s.status === 'atrasado')
          .sort((a, b) => (a.week_number || 0) - (b.week_number || 0));
        setPendingWeeks(pendingList);
        const pending = pendingList[0];
        if (pending) {
          setNextPending(pending);
          setSelectedWeek(pending);
        }
        if (weeklyRes?.data) {
          setWeeklyStatus(weeklyRes.data);
          // Auto-set amount to TOTAL PENDIENTE (rent + mora + accesorios)
          const totalPending = weeklyRes.data.total_pending;
          if (totalPending !== undefined && totalPending !== null) {
            setFormData((prev) => ({ ...prev, contract_id: contractId, amount: totalPending > 0 ? totalPending : (contract?.weekly_rent || '') }));
          }
        }
      } catch (err) {
        console.warn('[Payments] Could not load weekly status for contract', contractId, err);
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || 'Desconocido';
  };

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    return vehicle ? `${vehicle.brand} ${vehicle.sub_brand}` : 'Desconocido';
  };

  const activeContracts = contracts.filter((c) => c.status === 'activo');

  // Available months from payment data
  const MONTH_NAMES = {
    '01': 'Enero', '02': 'Febrero', '03': 'Marzo', '04': 'Abril',
    '05': 'Mayo', '06': 'Junio', '07': 'Julio', '08': 'Agosto',
    '09': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre'
  };
  const availableMonths = [...new Set(
    payments
      .filter(p => p.payment_date)
      .map(p => p.payment_date.substring(0, 7))
  )].sort().reverse();

  const filteredPayments = payments.filter((p) => {
    const clientName = getClientName(p.client_id).toLowerCase();
    if (!clientName.includes(searchTerm.toLowerCase())) return false;
    if (filterMonth && p.payment_date && !p.payment_date.startsWith(filterMonth)) return false;
    if (filterDate && p.payment_date && !p.payment_date.startsWith(filterDate)) return false;
    if (filterWeek !== '' && String(p.week_number) !== String(filterWeek)) return false;
    return true;
  });

  const totalReceived = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalAccessory = filteredPayments.reduce((sum, p) => sum + (p.accessory_amount || 0), 0);
  const totalPenalty = filteredPayments.reduce((sum, p) => sum + (p.penalty_amount || 0), 0);
  const isFiltered = filterMonth !== '' || filterDate !== '' || filterWeek !== '';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Pagos</h1>
          <p className="text-[#64748B]">{payments.length} pagos registrados</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => setShowDuplicatesModal(true)}
              className="btn-secondary flex items-center gap-2"
              data-testid="open-duplicates-modal-btn"
              title="Valida que no existan semanas con pagos repetidos en todos los contratos"
            >
              <Warning size={18} />
              Validar semanas repetidas
            </button>
          )}
          <button
            onClick={openStatementModal}
            className="btn-secondary flex items-center gap-2"
            data-testid="open-statement-modal-btn"
            title="Genera estado de cuenta mensual o semanal por cliente"
          >
            <FileText size={18} />
            Estado de Cuenta
          </button>
          {canEdit && (
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="add-payment-btn"
            >
              <Plus size={18} />
              Registrar Pago
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger flex items-center gap-2">
          <Warning size={20} />
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#E2E8F0]">
        <button
          onClick={() => setActiveTab('pagos')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'pagos' ? 'border-[#0055FF] text-[#0055FF]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}
          data-testid="tab-pagos"
        >
          <Money size={16} weight={activeTab === 'pagos' ? 'fill' : 'regular'} />
          Registros de Pagos
        </button>
        <button
          onClick={() => setActiveTab('cobranza-semanal')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'cobranza-semanal' ? 'border-[#0055FF] text-[#0055FF]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}
          data-testid="tab-cobranza-semanal"
        >
          <Receipt size={16} weight={activeTab === 'cobranza-semanal' ? 'fill' : 'regular'} />
          Cobranza Semanal
        </button>
      </div>

      {activeTab === 'cobranza-semanal' && (
        <WeeklyCollectionBoard
          board={weeklyBoard}
          loading={weeklyBoardLoading}
          search={weeklyBoardSearch}
          setSearch={setWeeklyBoardSearch}
          onRefresh={loadWeeklyBoard}
          formatCurrency={formatCurrency}
        />
      )}

      {activeTab === 'pagos' && (<>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="kpi-card">
          <p className="kpi-label">{isFiltered ? 'Pagos del Mes' : 'Total Pagos'}</p>
          <p className="text-2xl font-bold text-[#0F172A]">{filteredPayments.length}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">{isFiltered ? 'Rentas cobradas' : 'Total Rentas'}</p>
          <p className="text-xl font-bold text-[#10B981]" data-testid="kpi-rent-total">{formatCurrency(totalReceived)}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">{isFiltered ? 'Accesorios del mes' : 'Total Accesorios'}</p>
          <p className="text-xl font-bold text-[#0055FF]" data-testid="kpi-accessory-total">{formatCurrency(totalAccessory)}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">{isFiltered ? 'Mora cobrada' : 'Total Penalizaciones'}</p>
          <p className="text-xl font-bold text-[#E11D48]" data-testid="kpi-penalty-total">{formatCurrency(totalPenalty)}</p>
        </div>
        <button
          onClick={() => setShowPendingModal(true)}
          className="kpi-card text-left hover:bg-amber-50 transition-colors border border-transparent hover:border-amber-300"
          data-testid="kpi-pending-collection"
          title="Click para ver detalle"
        >
          <p className="kpi-label flex items-center gap-1">
            <Receipt size={12} />
            Por Cobrar Hoy
          </p>
          <p className="text-xl font-bold text-[#F59E0B]" data-testid="kpi-pending-total">
            {pendingCollection ? formatCurrency(pendingCollection.total_to_collect) : (pendingLoading ? '…' : '$0.00')}
          </p>
          {pendingCollection && (
            <p className="text-xs text-[#94A3B8] mt-0.5">
              {pendingCollection.unique_drivers} conductores · {pendingCollection.items.length} semanas
            </p>
          )}
        </button>
        <div className="kpi-card">
          <p className="kpi-label">Contratos Activos</p>
          <p className="text-2xl font-bold text-[#0055FF]">{activeContracts.length}</p>
        </div>
      </div>

      {/* Cobranza pendiente — sección colapsable */}
      {pendingCollection && pendingCollection.items.length > 0 && (
        <div className="card border-l-4 border-[#F59E0B]" data-testid="pending-collection-section">
          <button
            onClick={() => setPendingExpanded(!pendingExpanded)}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-amber-50/50 transition-colors"
            data-testid="toggle-pending-collection"
          >
            <div className="flex items-center gap-3">
              <Receipt size={20} className="text-[#F59E0B]" />
              <div className="text-left">
                <h3 className="text-base font-bold text-[#0F172A]">
                  Cobranza pendiente esta semana (acumulado hasta hoy)
                </h3>
                <p className="text-sm text-[#64748B]">
                  Falta cobrar <span className="font-bold text-[#F59E0B]">{formatCurrency(pendingCollection.total_to_collect)}</span>
                  {' '}de{' '}
                  <span className="font-medium">{pendingCollection.unique_drivers} conductores</span>
                  {' '}({pendingCollection.items.length} semanas)
                </p>
              </div>
            </div>
            {pendingExpanded ? <CaretUp size={20} /> : <CaretDown size={20} />}
          </button>

          {pendingExpanded && (
            <div className="border-t border-[#E2E8F0] p-5 space-y-4">
              {/* Toggle view mode */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[#64748B]">Agrupar por:</span>
                  <button
                    onClick={() => setPendingViewMode('contract')}
                    className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                      pendingViewMode === 'contract'
                        ? 'bg-[#0055FF] text-white font-medium'
                        : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
                    }`}
                    data-testid="pending-view-contract"
                  >
                    Semana del contrato
                  </button>
                  <button
                    onClick={() => setPendingViewMode('calendar')}
                    className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                      pendingViewMode === 'calendar'
                        ? 'bg-[#0055FF] text-white font-medium'
                        : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
                    }`}
                    data-testid="pending-view-calendar"
                  >
                    Semana del calendario
                  </button>
                </div>
                <button
                  onClick={() => setShowPendingModal(true)}
                  className="text-xs text-[#0055FF] hover:underline flex items-center gap-1"
                  data-testid="open-pending-modal"
                >
                  <Eye size={14} />
                  Ver detalle completo
                </button>
              </div>

              {/* Summary by week */}
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="pending-week-summary">
                  <thead>
                    <tr>
                      <th>{pendingViewMode === 'contract' ? 'Semana contrato' : 'Semana calendario'}</th>
                      <th className="text-right">Conductores</th>
                      <th className="text-right">Semanas pendientes</th>
                      <th className="text-right">Rentas</th>
                      <th className="text-right">Mora</th>
                      <th className="text-right">Total a cobrar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(pendingViewMode === 'contract'
                      ? pendingCollection.by_contract_week
                      : pendingCollection.by_calendar_week
                    ).map((b) => (
                      <tr key={pendingViewMode === 'contract' ? `cw-${b.contract_week}` : `calw-${b.calendar_week}`}>
                        <td className="font-semibold">
                          {pendingViewMode === 'contract' ? `Semana ${b.contract_week}` : b.calendar_week}
                        </td>
                        <td className="text-right">{b.drivers_count}</td>
                        <td className="text-right">{b.weeks_count}</td>
                        <td className="text-right">{formatCurrency(b.total_amount)}</td>
                        <td className="text-right text-[#E11D48]">{formatCurrency(b.total_penalty)}</td>
                        <td className="text-right font-bold text-[#F59E0B]">{formatCurrency(b.total_to_collect)}</td>
                      </tr>
                    ))}
                    <tr className="bg-[#F8FAFC]">
                      <td className="font-bold">TOTAL</td>
                      <td className="text-right font-bold">{pendingCollection.unique_drivers}</td>
                      <td className="text-right font-bold">{pendingCollection.items.length}</td>
                      <td className="text-right font-bold">{formatCurrency(pendingCollection.total_amount)}</td>
                      <td className="text-right font-bold text-[#E11D48]">{formatCurrency(pendingCollection.total_penalty)}</td>
                      <td className="text-right font-bold text-[#F59E0B]">{formatCurrency(pendingCollection.total_to_collect)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Drivers list */}
              <div>
                <h4 className="text-sm font-semibold text-[#0F172A] mb-2">Conductores con pagos pendientes</h4>
                <div className="overflow-x-auto max-h-80 overflow-y-auto border border-[#E2E8F0] rounded-md">
                  <table className="data-table" data-testid="pending-drivers-table">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr>
                        <th>Conductor</th>
                        <th>Vehículo</th>
                        <th>Semana contrato</th>
                        <th>Semana calendario</th>
                        <th>Vencimiento</th>
                        <th className="text-right">Renta</th>
                        <th className="text-right">Pagado</th>
                        <th className="text-right">Saldo</th>
                        <th className="text-right">Mora</th>
                        <th className="text-right">Accesorios</th>
                        <th className="text-right">Días atraso</th>
                        <th className="text-right">Total</th>
                        {isAdmin && <th className="text-center">Acciones</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {pendingCollection.items.map((it) => (
                        <tr key={it.schedule_id} data-testid={`pending-row-${it.schedule_id}`}>
                          <td className="font-medium">
                            {it.client_name}
                            {it.is_partial && (
                              <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold" title="Pago parcial registrado">PARCIAL</span>
                            )}
                          </td>
                          <td className="text-sm">{it.vehicle_label} <span className="text-[#94A3B8]">({it.vehicle_plates})</span></td>
                          <td>Sem {it.contract_week}</td>
                          <td className="text-sm">{it.calendar_week}</td>
                          <td className="text-sm">{it.due_date}</td>
                          <td className="text-right">{formatCurrency(it.expected_amount)}</td>
                          <td className="text-right text-emerald-600">{formatCurrency(it.paid_partial || 0)}</td>
                          <td className="text-right font-semibold text-amber-700">{formatCurrency(it.pending_balance ?? it.expected_amount)}</td>
                          <td className="text-right text-[#E11D48]">
                            {formatCurrency(it.penalty_amount)}
                            {it.penalty_manual && (
                              <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1 rounded" title="Mora editada manualmente">M</span>
                            )}
                          </td>
                          <td className="text-right text-[#0055FF]">
                            {formatCurrency(it.accessory_pending || 0)}
                            {it.accessory_note && (
                              <span className="ml-1 text-[10px] text-[#94A3B8] block truncate max-w-[100px]" title={it.accessory_note}>
                                {it.accessory_note}
                              </span>
                            )}
                          </td>
                          <td className={`text-right ${it.days_late > 0 ? 'text-[#E11D48] font-medium' : 'text-[#94A3B8]'}`}>
                            {it.days_late}
                          </td>
                          <td className="text-right font-bold text-[#F59E0B]">{formatCurrency(it.total_due)}</td>
                          {isAdmin && (
                            <td className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => openEditPenalty(it)}
                                  className="p-1 text-[#E11D48] hover:bg-red-50 rounded"
                                  title="Editar mora"
                                  data-testid={`edit-penalty-${it.schedule_id}`}
                                >
                                  <PencilSimple size={14} />
                                </button>
                                {it.penalty_amount > 0 && (
                                  <button
                                    onClick={() => handleDeletePenalty(it)}
                                    className="p-1 text-[#E11D48] hover:bg-red-50 rounded"
                                    title="Condonar mora (poner $0)"
                                    data-testid={`delete-penalty-${it.schedule_id}`}
                                  >
                                    <Trash size={14} />
                                  </button>
                                )}
                                <span className="mx-1 text-[#CBD5E1]">|</span>
                                <button
                                  onClick={() => openEditAccessory(it)}
                                  className="p-1 text-[#0055FF] hover:bg-blue-50 rounded"
                                  title="Editar accesorios pendientes"
                                  data-testid={`edit-accessory-${it.schedule_id}`}
                                >
                                  <PencilSimple size={14} />
                                </button>
                                {it.accessory_pending > 0 && (
                                  <button
                                    onClick={() => handleDeleteAccessory(it)}
                                    className="p-1 text-[#0055FF] hover:bg-blue-50 rounded"
                                    title="Borrar accesorios"
                                    data-testid={`delete-accessory-${it.schedule_id}`}
                                  >
                                    <Trash size={14} />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-xs">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por cliente..."
            className="input pl-10"
            data-testid="search-payments-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-[#64748B]" />
          <select
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="input-field text-sm min-w-[180px]"
            data-testid="filter-month-payments"
          >
            <option value="">Todos los meses</option>
            {availableMonths.map(m => {
              const [y, mo] = m.split('-');
              return <option key={m} value={m}>{MONTH_NAMES[mo]} {y}</option>;
            })}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-[#64748B]" />
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="input-field text-sm"
            placeholder="Fecha exacta"
            data-testid="filter-date-payments"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={filterWeek}
            onChange={(e) => setFilterWeek(e.target.value)}
            min="1"
            placeholder="Semana #"
            className="input-field text-sm w-32"
            data-testid="filter-week-payments"
          />
        </div>
        {(filterMonth || filterDate || filterWeek !== '') && (
          <button
            onClick={() => { setFilterMonth(''); setFilterDate(''); setFilterWeek(''); }}
            className="text-sm text-[#0055FF] hover:underline"
            data-testid="clear-filters-btn"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="payments-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Vehículo</th>
                <th>Semana</th>
                <th>Monto</th>
                <th>Método</th>
                <th>Tipo</th>
                <th>Días Atraso</th>
                <th>Penalización</th>
                <th>Accesorio</th>
                {canEdit && <th className="text-right">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 11 : 10} className="text-center py-8 text-[#64748B]">
                    No se encontraron pagos
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment) => (
                  <tr key={payment.id} data-testid={`payment-row-${payment.id}`}>
                    <td>{formatDate(payment.payment_date)}</td>
                    <td className="font-medium">{getClientName(payment.client_id)}</td>
                    <td>{getVehicleInfo(payment.vehicle_id)}</td>
                    <td>Semana {payment.week_number}</td>
                    <td className="font-bold text-[#10B981]">
                      {formatCurrency(payment.amount)}
                      {Array.isArray(payment.installments) && payment.installments.length > 1 && (
                        <span
                          className="ml-1 text-[10px] bg-blue-50 text-[#0055FF] px-1.5 py-0.5 rounded-full font-semibold align-middle"
                          title={payment.installments.map((i) => `${(i.payment_date || '').slice(0, 10)}: ${formatCurrency(i.amount)}`).join('\n')}
                          data-testid={`installments-badge-${payment.id}`}
                        >
                          {payment.installments.length} abonos
                        </span>
                      )}
                      {payment.is_partial && (
                        <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold align-middle" title="Semana aún no liquidada">PARCIAL</span>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-neutral capitalize">{payment.payment_method}</span>
                    </td>
                    <td>
                      <span className={`badge ${
                        payment.payment_type === 'renta_normal' ? 'badge-info' :
                        payment.payment_type === 'moratorio' ? 'badge-warning' : 'badge-neutral'
                      }`}>
                        {payment.payment_type === 'renta_normal' ? 'Renta' :
                         payment.payment_type === 'moratorio' ? 'Moratorio' : payment.payment_type}
                      </span>
                    </td>
                    <td className={payment.days_late > 0 ? 'text-[#E11D48]' : ''}>
                      {payment.days_late || 0}
                    </td>
                    <td className={payment.penalty_amount > 0 ? 'text-[#E11D48]' : ''}>
                      {formatCurrency(payment.penalty_amount || 0)}
                    </td>
                    <td className={payment.accessory_amount > 0 ? 'text-[#0055FF] font-medium' : 'text-[#64748B]'}>
                      {formatCurrency(payment.accessory_amount || 0)}
                    </td>
                    {canEdit && (
                      <td className="text-right whitespace-nowrap">
                        <button
                          onClick={() => setEditPayment({
                            id: payment.id,
                            client_name: getClientName(payment.client_id),
                            week_number: payment.week_number,
                            amount: payment.amount || 0,
                            penalty_amount: payment.penalty_amount || 0,
                            accessory_amount: payment.accessory_amount || 0,
                            days_late: payment.days_late || 0,
                            notes: payment.notes || ''
                          })}
                          className="p-1.5 text-[#64748B] hover:text-[#0055FF] hover:bg-blue-50 rounded text-xs"
                          title="Editar pago"
                          data-testid={`edit-payment-${payment.id}`}
                        >
                          Editar
                        </button>
                        {(payment.penalty_amount > 0 || payment.days_late > 0) && (
                          <button
                            onClick={() => handleClearPenalty(payment.id)}
                            className="p-1.5 text-[#E11D48] hover:bg-red-50 rounded text-xs ml-1"
                            title="Eliminar penalización"
                            data-testid={`clear-penalty-${payment.id}`}
                          >
                            Quitar mora
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => handleDeletePayment(payment)}
                            disabled={deletingPaymentId === payment.id}
                            className="p-1.5 text-[#E11D48] hover:bg-red-50 rounded text-xs ml-1 disabled:opacity-50"
                            title="Eliminar pago (revierte saldo del contrato)"
                            data-testid={`delete-payment-${payment.id}`}
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>)}

      {/* Modal */}
      {showDuplicatesModal && (
        <DuplicateWeeksModal
          onClose={() => setShowDuplicatesModal(false)}
          onMerged={loadData}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
        />
      )}
      {showModal && (
        <div className="modal-overlay" data-testid="payment-modal">
          <div className="modal-content animate-fade-in">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-heading font-bold">Registrar Pago</h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedContract(null);
                    setNextPending(null);
                    setPendingWeeks([]);
                    setSelectedWeek(null);
                    setWeeklyStatus(null);
                    setInlineEdit(null);
                    setFormData({
                      contract_id: '',
                      amount: '',
                      payment_method: 'transferencia',
                      payment_type: 'renta_normal',
                      payment_date: todayStr,
                      notes: ''
                    });
                  }}
                  className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="alert alert-danger text-sm">{formError}</div>
              )}

              <div>
                <label className="label">Contrato *</label>
                <select
                  value={formData.contract_id}
                  onChange={(e) => handleContractChange(e.target.value)}
                  className="input"
                  required
                  data-testid="payment-contract-select"
                >
                  <option value="">Seleccionar contrato...</option>
                  {activeContracts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {getClientName(c.client_id)} - {getVehicleInfo(c.vehicle_id)}
                    </option>
                  ))}
                </select>
              </div>

              {selectedContract && pendingWeeks.length > 0 && (
                <div>
                  <label className="label">Semana a pagar *</label>
                  <select
                    value={selectedWeek?.id || ''}
                    onChange={(e) => handleWeekChange(e.target.value)}
                    className="input"
                    data-testid="payment-week-select"
                  >
                    {pendingWeeks.map((s) => {
                      const balance = Math.max(0, (s.expected_amount || 0) - (s.paid_amount || 0));
                      return (
                        <option key={s.id} value={s.id} data-testid={`payment-week-option-${s.week_number}`}>
                          Semana {s.week_number} · Vence {formatDate(s.due_date)} · Saldo {formatCurrency(balance)}
                          {s.status === 'atrasado' ? ' · ATRASADA' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-xs text-[#64748B] mt-1">
                    {pendingWeeks.length} semana{pendingWeeks.length !== 1 ? 's' : ''} pendiente{pendingWeeks.length !== 1 ? 's' : ''}. Por defecto se selecciona la más antigua.
                  </p>
                </div>
              )}

              {selectedContract && weeklyStatus && weeklyStatus.week_number != null && (
                <div
                  className={`p-4 rounded-lg border-2 ${weeklyStatus.total_pending > 0 ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50'}`}
                  data-testid="weekly-status-card"
                >
                  <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-2">
                    Cobro Semana {weeklyStatus.week_number}
                    {weeklyStatus.due_date ? ` · Vence ${formatDate(weeklyStatus.due_date)}` : ''}
                  </div>

                  {/* Desglose */}
                  <div className="bg-white/60 rounded-md p-3 space-y-1 text-sm mb-3">
                    <div className="flex justify-between">
                      <span className="text-[#475569]">Renta pendiente</span>
                      <span className="font-semibold text-[#0F172A]" data-testid="weekly-breakdown-rent">
                        {formatCurrency(weeklyStatus.pending_this_week)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#475569]">
                        Mora pendiente
                        {weeklyStatus.penalty_expected > 0 ? (
                          <span className="ml-1 text-[10px] text-[#94A3B8]">
                            (de {formatCurrency(weeklyStatus.penalty_expected)}, pagado {formatCurrency(weeklyStatus.penalty_paid || 0)})
                          </span>
                        ) : null}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className={`font-semibold ${weeklyStatus.penalty_amount > 0 ? 'text-red-600' : 'text-[#0F172A]'}`} data-testid="weekly-breakdown-penalty">
                          {formatCurrency(weeklyStatus.penalty_amount || 0)}
                        </span>
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => {
                              setInlineEdit('penalty');
                              setInlinePenaltyValue(String(weeklyStatus.penalty_expected || 0));
                            }}
                            className="text-[10px] text-[#0055FF] hover:underline font-medium"
                            data-testid="edit-penalty-inline"
                          >
                            Ajustar
                          </button>
                        )}
                      </span>
                    </div>
                    {inlineEdit === 'penalty' && (
                      <div className="flex items-center gap-2 py-1 pl-2 border-l-2 border-red-300 bg-red-50/50 rounded-r-md">
                        <input
                          type="number"
                          step="0.01"
                          value={inlinePenaltyValue}
                          onChange={(e) => setInlinePenaltyValue(e.target.value)}
                          className="input py-1 text-xs w-32"
                          placeholder="Nueva mora"
                          data-testid="inline-penalty-input"
                        />
                        <button type="button" onClick={savePenaltyInline} disabled={inlineSaving}
                          className="text-xs bg-[#0055FF] text-white px-2 py-1 rounded hover:bg-blue-700 disabled:opacity-50" data-testid="inline-penalty-save">
                          Guardar
                        </button>
                        <button type="button" onClick={condonePenaltyInline} disabled={inlineSaving}
                          className="text-xs bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-700 disabled:opacity-50" data-testid="inline-penalty-condone">
                          Condonar
                        </button>
                        <button type="button" onClick={() => setInlineEdit(null)}
                          className="text-xs text-[#64748B] hover:text-[#0F172A]">
                          Cancelar
                        </button>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-[#475569]">
                        Accesorios pendientes
                        {weeklyStatus.accessory_note ? (
                          <span className="ml-1 text-[10px] text-[#94A3B8]">({weeklyStatus.accessory_note})</span>
                        ) : null}
                        {weeklyStatus.accessory_expected > 0 ? (
                          <span className="ml-1 text-[10px] text-[#94A3B8]">
                            (de {formatCurrency(weeklyStatus.accessory_expected)}, pagado {formatCurrency(weeklyStatus.accessory_paid || 0)})
                          </span>
                        ) : null}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className={`font-semibold ${weeklyStatus.accessory_pending > 0 ? 'text-purple-700' : 'text-[#0F172A]'}`} data-testid="weekly-breakdown-accessory">
                          {formatCurrency(weeklyStatus.accessory_pending || 0)}
                        </span>
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => {
                              setInlineEdit('accessory');
                              setInlineAccessoryValue(String(weeklyStatus.accessory_expected || 0));
                              setInlineAccessoryNote(weeklyStatus.accessory_note || '');
                            }}
                            className="text-[10px] text-[#0055FF] hover:underline font-medium"
                            data-testid="edit-accessory-inline"
                          >
                            Ajustar
                          </button>
                        )}
                      </span>
                    </div>
                    {inlineEdit === 'accessory' && (
                      <div className="py-1 pl-2 border-l-2 border-purple-300 bg-purple-50/50 rounded-r-md space-y-1">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.01"
                            value={inlineAccessoryValue}
                            onChange={(e) => setInlineAccessoryValue(e.target.value)}
                            className="input py-1 text-xs w-32"
                            placeholder="Nuevo accesorio"
                            data-testid="inline-accessory-input"
                          />
                          <input
                            type="text"
                            value={inlineAccessoryNote}
                            onChange={(e) => setInlineAccessoryNote(e.target.value)}
                            className="input py-1 text-xs flex-1"
                            placeholder="Nota (ej: casco, tarjeta…)"
                            data-testid="inline-accessory-note"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={saveAccessoryInline} disabled={inlineSaving}
                            className="text-xs bg-[#0055FF] text-white px-2 py-1 rounded hover:bg-blue-700 disabled:opacity-50" data-testid="inline-accessory-save">
                            Guardar
                          </button>
                          <button type="button" onClick={removeAccessoryInline} disabled={inlineSaving}
                            className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 disabled:opacity-50" data-testid="inline-accessory-remove">
                            Quitar
                          </button>
                          <button type="button" onClick={() => setInlineEdit(null)}
                            className="text-xs text-[#64748B] hover:text-[#0F172A]">
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between pt-1 border-t border-[#E2E8F0] mt-1">
                      <span className="font-bold text-[#0F172A]">TOTAL A PAGAR</span>
                      <span className={`font-bold text-lg ${weeklyStatus.total_pending > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="weekly-breakdown-total">
                        {formatCurrency(weeklyStatus.total_pending)}
                      </span>
                    </div>
                  </div>

                  {/* Mini-historial: abonos previos de esta semana */}
                  {Array.isArray(weeklyStatus.previous_payments) && weeklyStatus.previous_payments.length > 0 && (
                    <div className="bg-white/60 rounded-md p-3 mt-3" data-testid="weekly-partials-list">
                      <div className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-2 flex items-center justify-between">
                        <span>Abonos previos de esta semana</span>
                        <span className="text-[#0055FF] normal-case">{weeklyStatus.previous_payments.length} abono{weeklyStatus.previous_payments.length !== 1 ? 's' : ''}</span>
                      </div>
                      <ul className="space-y-1 text-sm">
                        {weeklyStatus.previous_payments.map((p) => (
                          <li key={p.id} className="flex items-center justify-between text-[#0F172A]" data-testid={`partial-payment-${p.id}`}>
                            <span className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              <span className="text-[#475569]">
                                {formatDate(p.payment_date)}
                                {p.payment_method ? <span className="ml-1 text-[10px] uppercase tracking-wide text-[#94A3B8]">· {p.payment_method}</span> : null}
                              </span>
                            </span>
                            <span className="font-semibold text-emerald-700">{formatCurrency(p.amount)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* KPIs históricos (renta) */}
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-[10px] text-[#94A3B8] uppercase">Renta</p>
                      <p className="text-sm font-bold text-[#0F172A]" data-testid="weekly-expected">
                        {formatCurrency(weeklyStatus.expected_amount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#94A3B8] uppercase">Pagado</p>
                      <p className="text-sm font-bold text-emerald-600" data-testid="weekly-paid">
                        {formatCurrency(weeklyStatus.paid_this_week)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#94A3B8] uppercase">Falta renta</p>
                      <p
                        className={`text-sm font-bold ${weeklyStatus.pending_this_week > 0 ? 'text-red-600' : 'text-emerald-600'}`}
                        data-testid="weekly-pending"
                      >
                        {formatCurrency(weeklyStatus.pending_this_week)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {selectedContract && (
                <div className="p-4 bg-[#F8FAFC] rounded-lg space-y-2" data-testid="contract-info-box">
                  <p className="text-sm text-[#64748B]">
                    Renta semanal: <span className="font-medium text-[#0F172A]">{formatCurrency(selectedContract.weekly_rent)}</span>
                  </p>
                  <p className="text-sm text-[#64748B]">
                    Saldo actual: <span className="font-medium text-[#0F172A]">{formatCurrency(selectedContract.current_balance)}</span>
                  </p>
                  {nextPending ? (
                    <p className="text-sm text-[#64748B]" data-testid="next-week-info">
                      Próxima semana a cubrir:{' '}
                      <span className="font-semibold text-[#0F172A]">Semana {nextPending.week_number}</span>
                      {' '}— Vence {formatDate(nextPending.due_date)}
                    </p>
                  ) : (
                    <p className="text-sm text-[#16A34A]" data-testid="next-week-info">
                      Sin semanas pendientes
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="label">Fecha de Pago *</label>
                <input
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                  className="input"
                  required
                  data-testid="payment-date-input"
                />
                <p className="text-xs text-[#64748B] mt-1">
                  El pago se aplicará a la semana seleccionada arriba.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Monto *</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="input"
                    required
                    min="0"
                    step="0.01"
                    data-testid="payment-amount-input"
                  />
                </div>
                <div>
                  <label className="label">Método de Pago</label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    className="input"
                    data-testid="payment-method-select"
                  >
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="deposito">Depósito</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Tipo de Pago</label>
                <select
                  value={formData.payment_type}
                  onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
                  className="input"
                  data-testid="payment-type-select"
                >
                  <option value="renta_normal">Renta Normal</option>
                  <option value="adelanto">Adelanto</option>
                  <option value="moratorio">Pago Moratorio</option>
                  <option value="deposito">Depósito</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Penalización cobrada (opcional)</label>
                  <input
                    type="number"
                    value={formData.penalty_amount}
                    onChange={(e) => setFormData({ ...formData, penalty_amount: e.target.value })}
                    className="input"
                    min="0"
                    step="0.01"
                    placeholder="Dejar vacío para auto-cálculo"
                    data-testid="payment-penalty-input"
                  />
                  <p className="text-xs text-[#64748B] mt-1">
                    Si lo dejas vacío, se calculará por días de atraso × $100.
                  </p>
                </div>
                <div>
                  <label className="label">Accesorio cobrado (opcional)</label>
                  <input
                    type="number"
                    value={formData.accessory_amount}
                    onChange={(e) => setFormData({ ...formData, accessory_amount: e.target.value })}
                    className="input"
                    min="0"
                    step="0.01"
                    placeholder="0"
                    data-testid="payment-accessory-input"
                  />
                  <p className="text-xs text-[#64748B] mt-1">
                    Ej. comisión, recargo administrativo, etc.
                  </p>
                </div>
              </div>

              <div>
                <label className="label">Notas (opcional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input"
                  rows={2}
                  placeholder="Observaciones del pago..."
                  data-testid="payment-notes-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                  data-testid="save-payment-btn"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check size={18} />
                  )}
                  Registrar Pago
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Payment Modal */}
      {editPayment && (
        <div className="modal-overlay" data-testid="edit-payment-modal">
          <div className="modal-content animate-fade-in max-w-lg">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-heading font-bold">Editar Pago</h2>
                  <p className="text-sm text-[#64748B]">
                    {editPayment.client_name} — Semana {editPayment.week_number}
                  </p>
                </div>
                <button
                  onClick={() => setEditPayment(null)}
                  className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {formError && (
                <div className="alert alert-danger text-sm">{formError}</div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Monto</label>
                  <input
                    type="number"
                    value={editPayment.amount}
                    onChange={(e) => setEditPayment({ ...editPayment, amount: e.target.value })}
                    className="input"
                    min="0"
                    step="0.01"
                    data-testid="edit-amount-input"
                  />
                </div>
                <div>
                  <label className="label">Días de atraso</label>
                  <input
                    type="number"
                    value={editPayment.days_late}
                    onChange={(e) => setEditPayment({ ...editPayment, days_late: e.target.value })}
                    className="input"
                    min="0"
                    step="1"
                    data-testid="edit-days-late-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Penalización</label>
                  <input
                    type="number"
                    value={editPayment.penalty_amount}
                    onChange={(e) => setEditPayment({ ...editPayment, penalty_amount: e.target.value })}
                    className="input"
                    min="0"
                    step="0.01"
                    data-testid="edit-penalty-input"
                  />
                </div>
                <div>
                  <label className="label">Accesorio cobrado</label>
                  <input
                    type="number"
                    value={editPayment.accessory_amount}
                    onChange={(e) => setEditPayment({ ...editPayment, accessory_amount: e.target.value })}
                    className="input"
                    min="0"
                    step="0.01"
                    data-testid="edit-accessory-input"
                  />
                </div>
              </div>
              <div>
                <label className="label">Notas</label>
                <textarea
                  value={editPayment.notes}
                  onChange={(e) => setEditPayment({ ...editPayment, notes: e.target.value })}
                  className="input"
                  rows={2}
                  data-testid="edit-notes-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setEditPayment(null)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                  data-testid="save-edit-payment-btn"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check size={18} />
                  )}
                  Guardar cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statement Modal */}
      {showStatementModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="statement-modal"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <h2 className="text-xl font-heading font-bold text-[#0F172A] flex items-center gap-2">
                <FileText size={22} className="text-[#0055FF]" />
                Estado de Cuenta
              </h2>
              <button
                onClick={() => setShowStatementModal(false)}
                className="text-[#64748B] hover:text-[#0F172A]"
                aria-label="cerrar"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-[#64748B]">
                Genera el estado de cuenta de un cliente para el periodo seleccionado.
                Incluye: pagos del periodo, desglose por concepto, amortización del contrato
                y gráfico de cumplimiento.
              </p>

              <div>
                <label className="label">Cliente *</label>
                <select
                  value={statementForm.client_id}
                  onChange={(e) => setStatementForm({ ...statementForm, client_id: e.target.value, contract_id: '' })}
                  className="input"
                  data-testid="statement-client-select"
                >
                  <option value="">Seleccionar cliente...</option>
                  {clients
                    .filter((c) => contracts.some((co) => co.client_id === c.id))
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
              </div>

              {statementForm.client_id && (
                <div>
                  <label className="label">Contrato (opcional)</label>
                  <select
                    value={statementForm.contract_id}
                    onChange={(e) => setStatementForm({ ...statementForm, contract_id: e.target.value })}
                    className="input"
                    data-testid="statement-contract-select"
                  >
                    <option value="">Todos los contratos del cliente</option>
                    {contracts
                      .filter((c) => c.client_id === statementForm.client_id)
                      .map((c) => {
                        const v = vehicles.find((vv) => vv.id === c.vehicle_id);
                        const vehLabel = v ? `${v.brand} ${v.sub_brand} (${v.plates})` : c.id;
                        return (
                          <option key={c.id} value={c.id}>
                            {vehLabel} — {c.status}
                          </option>
                        );
                      })}
                  </select>
                </div>
              )}

              <div>
                <label className="label">Tipo de Periodo</label>
                <div className="flex gap-2" data-testid="statement-period-type">
                  <button
                    type="button"
                    onClick={() => setStatementForm({ ...statementForm, period_type: 'weekly' })}
                    className={
                      statementForm.period_type === 'weekly'
                        ? 'btn-primary flex-1'
                        : 'btn-secondary flex-1'
                    }
                    data-testid="statement-weekly-btn"
                  >
                    Semanal
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatementForm({ ...statementForm, period_type: 'monthly' })}
                    className={
                      statementForm.period_type === 'monthly'
                        ? 'btn-primary flex-1'
                        : 'btn-secondary flex-1'
                    }
                    data-testid="statement-monthly-btn"
                  >
                    Mensual
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Desde *</label>
                  <input
                    type="date"
                    value={statementForm.start_date}
                    onChange={(e) => setStatementForm({ ...statementForm, start_date: e.target.value })}
                    className="input"
                    data-testid="statement-start-date"
                  />
                </div>
                <div>
                  <label className="label">Hasta *</label>
                  <input
                    type="date"
                    value={statementForm.end_date}
                    onChange={(e) => setStatementForm({ ...statementForm, end_date: e.target.value })}
                    className="input"
                    data-testid="statement-end-date"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowStatementModal(false)}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleDownloadStatement('excel')}
                disabled={statementLoading || !statementForm.client_id}
                className="btn-secondary flex items-center gap-2 disabled:opacity-50"
                data-testid="download-statement-excel-btn"
              >
                <FileXls size={18} />
                {statementLoading ? 'Generando…' : 'Descargar Excel'}
              </button>
              <button
                type="button"
                onClick={() => handleDownloadStatement('pdf')}
                disabled={statementLoading || !statementForm.client_id}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
                data-testid="download-statement-pdf-btn"
              >
                <FilePdf size={18} />
                {statementLoading ? 'Generando…' : 'Descargar PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Penalty Modal (cobranza pendiente) */}
      {editPenaltyModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="edit-penalty-modal"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <h2 className="text-lg font-heading font-bold text-[#0F172A] flex items-center gap-2">
                <PencilSimple size={20} className="text-[#E11D48]" />
                Editar mora
              </h2>
              <button onClick={() => setEditPenaltyModal(null)} className="text-[#64748B] hover:text-[#0F172A]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-[#64748B]">
                <p><span className="font-medium text-[#0F172A]">{editPenaltyModal.client_name}</span></p>
                <p>Semana {editPenaltyModal.contract_week} · Mora actual: <span className="font-medium">{formatCurrency(editPenaltyModal.current_penalty)}</span></p>
              </div>
              <div>
                <label className="label">Nuevo monto de mora *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editPenaltyModal.new_penalty}
                  onChange={(e) => setEditPenaltyModal({ ...editPenaltyModal, new_penalty: e.target.value })}
                  className="input"
                  data-testid="edit-penalty-input"
                  autoFocus
                />
                <p className="text-xs text-[#94A3B8] mt-1">
                  Al editar manualmente, el cron de penalizaciones NO recalculará este monto.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-2">
              <button onClick={() => setEditPenaltyModal(null)} className="btn-secondary">Cancelar</button>
              <button
                onClick={submitEditPenalty}
                disabled={savingSchedule}
                className="btn-primary disabled:opacity-50"
                data-testid="save-penalty-btn"
              >
                {savingSchedule ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Accessory Modal (cobranza pendiente) */}
      {editAccessoryModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="edit-accessory-modal"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <h2 className="text-lg font-heading font-bold text-[#0F172A] flex items-center gap-2">
                <PencilSimple size={20} className="text-[#0055FF]" />
                Accesorios pendientes
              </h2>
              <button onClick={() => setEditAccessoryModal(null)} className="text-[#64748B] hover:text-[#0F172A]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-[#64748B]">
                <p><span className="font-medium text-[#0F172A]">{editAccessoryModal.client_name}</span></p>
                <p>Semana {editAccessoryModal.contract_week} · Accesorios actuales: <span className="font-medium">{formatCurrency(editAccessoryModal.current_accessory || 0)}</span></p>
              </div>
              <div>
                <label className="label">Monto de accesorios pendientes *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editAccessoryModal.new_amount}
                  onChange={(e) => setEditAccessoryModal({ ...editAccessoryModal, new_amount: e.target.value })}
                  className="input"
                  data-testid="edit-accessory-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="label">Nota / Descripción</label>
                <input
                  type="text"
                  value={editAccessoryModal.new_note}
                  onChange={(e) => setEditAccessoryModal({ ...editAccessoryModal, new_note: e.target.value })}
                  className="input"
                  placeholder="Ej: Casco perdido"
                  data-testid="edit-accessory-note"
                  maxLength={120}
                />
                <p className="text-xs text-[#94A3B8] mt-1">
                  Este monto se sumará automáticamente al registrar el pago de esta cuota.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-2">
              <button onClick={() => setEditAccessoryModal(null)} className="btn-secondary">Cancelar</button>
              <button
                onClick={submitEditAccessory}
                disabled={savingSchedule}
                className="btn-primary disabled:opacity-50"
                data-testid="save-accessory-btn"
              >
                {savingSchedule ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Collection Modal */}
      {showPendingModal && pendingCollection && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="pending-collection-modal"
        >
          <div className="bg-white rounded-xl shadow-xl w-[95vw] max-w-[1600px] max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <h2 className="text-xl font-heading font-bold text-[#0F172A] flex items-center gap-2">
                <Receipt size={22} className="text-[#F59E0B]" />
                Cobranza Pendiente — Detalle al {pendingCollection.today}
              </h2>
              <button
                onClick={() => setShowPendingModal(false)}
                className="text-[#64748B] hover:text-[#0F172A]"
                aria-label="cerrar"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-5">
              {/* Búsqueda rápida + KPIs filtrados */}
              {(() => {
                const q = pendingModalSearch.trim().toLowerCase();
                const filtered = q
                  ? pendingCollection.items.filter(
                      (it) =>
                        (it.client_name || '').toLowerCase().includes(q) ||
                        (it.vehicle_plates || '').toLowerCase().includes(q) ||
                        (it.vehicle_label || '').toLowerCase().includes(q)
                    )
                  : pendingCollection.items;
                const fTotalRent = filtered.reduce((s, it) => s + ((it.pending_balance ?? it.expected_amount) || 0), 0);
                const fTotalPen = filtered.reduce((s, it) => s + (it.penalty_amount || 0), 0);
                const fTotalAcc = filtered.reduce((s, it) => s + (it.accessory_pending || 0), 0);
                const fTotalDue = filtered.reduce((s, it) => s + (it.total_due || 0), 0);
                const fDrivers = new Set(filtered.map((it) => it.client_id).filter(Boolean)).size;
                return (
                  <>
                    {/* Search bar */}
                    <div className="relative">
                      <MagnifyingGlass
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none"
                      />
                      <input
                        type="text"
                        placeholder="Buscar por conductor, placa o vehículo..."
                        value={pendingModalSearch}
                        onChange={(e) => setPendingModalSearch(e.target.value)}
                        className="input pl-10 pr-10"
                        data-testid="pending-modal-search"
                        autoFocus
                      />
                      {pendingModalSearch && (
                        <button
                          onClick={() => setPendingModalSearch('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]"
                          aria-label="Limpiar búsqueda"
                          data-testid="pending-modal-search-clear"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>

                    {/* KPIs (live-filtered) */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="kpi-card bg-amber-50/50">
                        <p className="kpi-label">Total a Cobrar</p>
                        <p className="text-2xl font-bold text-[#F59E0B]" data-testid="modal-kpi-total">
                          {formatCurrency(fTotalDue)}
                        </p>
                      </div>
                      <div className="kpi-card">
                        <p className="kpi-label">Rentas</p>
                        <p className="text-xl font-bold text-[#0F172A]">{formatCurrency(fTotalRent)}</p>
                      </div>
                      <div className="kpi-card">
                        <p className="kpi-label">Mora + Accesorios</p>
                        <p className="text-xl font-bold text-[#E11D48]">
                          {formatCurrency(fTotalPen + fTotalAcc)}
                        </p>
                      </div>
                      <div className="kpi-card">
                        <p className="kpi-label">Conductores</p>
                        <p className="text-2xl font-bold text-[#0055FF]">{fDrivers}</p>
                        <p className="text-xs text-[#94A3B8] mt-0.5">
                          {filtered.length} semanas pendientes
                        </p>
                      </div>
                    </div>

                    {/* Driver detail full table */}
                    <div>
                      <h4 className="text-sm font-semibold text-[#0F172A] mb-2 flex items-center justify-between">
                        <span>Detalle por conductor</span>
                        {q && (
                          <span className="text-xs font-normal text-[#64748B]">
                            Filtrado: {filtered.length} de {pendingCollection.items.length}
                          </span>
                        )}
                      </h4>
                      <div className="overflow-x-auto border border-[#E2E8F0] rounded-md">
                        <table className="data-table text-xs" data-testid="modal-pending-drivers-table">
                          <thead className="bg-[#F8FAFC]">
                            <tr>
                              <th className="sticky left-0 bg-[#F8FAFC] z-10 min-w-[180px]">Conductor</th>
                              <th>Teléfono</th>
                              <th>Vehículo</th>
                              <th>Sem. Contrato</th>
                              <th>Sem. Calendario</th>
                              <th>Vencimiento</th>
                              <th className="text-right">Renta</th>
                              <th className="text-right">Pagado</th>
                              <th className="text-right">Saldo</th>
                              <th className="text-right">Mora</th>
                              <th className="text-right">Accesorios</th>
                              <th className="text-right">Días</th>
                              <th className="text-right">Total</th>
                              {isAdmin && <th className="text-center">Acciones</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={isAdmin ? 14 : 13}
                                  className="text-center text-[#94A3B8] py-8"
                                  data-testid="modal-pending-empty"
                                >
                                  Sin coincidencias para &ldquo;{pendingModalSearch}&rdquo;
                                </td>
                              </tr>
                            ) : filtered.map((it) => (
                              <tr key={it.schedule_id}>
                                <td className="sticky left-0 bg-white z-10 font-medium border-r border-[#E2E8F0]">
                                  {it.client_name}
                                  {it.is_partial && (
                                    <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">PARCIAL</span>
                                  )}
                                </td>
                                <td className="text-[#475569]">{it.client_phone || '-'}</td>
                                <td>{it.vehicle_label} <span className="text-[#94A3B8]">({it.vehicle_plates})</span></td>
                                <td>Sem {it.contract_week}</td>
                                <td>{it.calendar_week}</td>
                                <td>{it.due_date}</td>
                                <td className="text-right">{formatCurrency(it.expected_amount)}</td>
                                <td className="text-right text-emerald-600">{formatCurrency(it.paid_partial || 0)}</td>
                                <td className="text-right font-semibold text-amber-700">{formatCurrency(it.pending_balance ?? it.expected_amount)}</td>
                                <td className="text-right text-[#E11D48]">
                                  {formatCurrency(it.penalty_amount)}
                                  {it.penalty_manual && (
                                    <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1 rounded" title="Mora editada manualmente">M</span>
                                  )}
                                </td>
                                <td className="text-right text-[#0055FF]">
                                  {formatCurrency(it.accessory_pending || 0)}
                                  {it.accessory_note && (
                                    <div className="text-[10px] text-[#94A3B8] truncate max-w-[120px]" title={it.accessory_note}>
                                      {it.accessory_note}
                                    </div>
                                  )}
                                </td>
                                <td className={`text-right ${it.days_late > 0 ? 'text-[#E11D48] font-medium' : 'text-[#94A3B8]'}`}>
                                  {it.days_late}
                                </td>
                                <td className="text-right font-bold text-[#F59E0B]">{formatCurrency(it.total_due)}</td>
                                {isAdmin && (
                                  <td className="text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <button
                                        onClick={() => openEditPenalty(it)}
                                        className="p-1 text-[#E11D48] hover:bg-red-50 rounded"
                                        title="Editar mora"
                                      >
                                        <PencilSimple size={14} />
                                      </button>
                                      {it.penalty_amount > 0 && (
                                        <button
                                          onClick={() => handleDeletePenalty(it)}
                                          className="p-1 text-[#E11D48] hover:bg-red-50 rounded"
                                          title="Condonar mora"
                                        >
                                          <Trash size={14} />
                                        </button>
                                      )}
                                      <span className="mx-1 text-[#CBD5E1]">|</span>
                                      <button
                                        onClick={() => openEditAccessory(it)}
                                        className="p-1 text-[#0055FF] hover:bg-blue-50 rounded"
                                        title="Editar accesorios pendientes"
                                      >
                                        <PencilSimple size={14} />
                                      </button>
                                      {it.accessory_pending > 0 && (
                                        <button
                                          onClick={() => handleDeleteAccessory(it)}
                                          className="p-1 text-[#0055FF] hover:bg-blue-50 rounded"
                                          title="Borrar accesorios"
                                        >
                                          <Trash size={14} />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={handleExportPendingFiltered}
                disabled={pendingExporting || pendingFiltered.length === 0}
                className="btn-secondary flex items-center gap-2 disabled:opacity-50"
                data-testid="export-pending-excel-btn"
                title={pendingModalSearch ? `Exporta solo los ${pendingFiltered.length} filtrados` : `Exporta los ${pendingFiltered.length} pendientes`}
              >
                <FileXls size={18} />
                {pendingExporting
                  ? 'Generando...'
                  : pendingModalSearch
                    ? `Exportar filtrado (${pendingFiltered.length}) a Excel`
                    : `Exportar (${pendingFiltered.length}) a Excel`}
              </button>
              <button
                type="button"
                onClick={() => setShowPendingModal(false)}
                className="btn-secondary"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;

// -------------------- WeeklyCollectionBoard --------------------
const WeeklyCollectionBoard = ({ board, loading, search, setSearch, onRefresh, formatCurrency }) => {
  const [expanded, setExpanded] = React.useState({});
  const toggle = (cid) => setExpanded((p) => ({ ...p, [cid]: !p[cid] }));

  if (loading || !board) {
    return (
      <div className="card p-8 text-center text-[#64748B]" data-testid="weekly-board-loading">
        Cargando cobranza semanal…
      </div>
    );
  }

  const q = (search || '').trim().toLowerCase();
  const filtered = q
    ? board.clients.filter((c) =>
        (c.client_name || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        c.contracts.some((k) => (k.vehicle_plates || '').toLowerCase().includes(q))
      )
    : board.clients;

  const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }) : '—');

  return (
    <div className="space-y-4" data-testid="weekly-board">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-heading font-bold text-[#0F172A]">Cobranza Semanal</h2>
          <p className="text-xs text-[#64748B]">
            Semana en curso: <strong>{fmtDate(board.week_start)}</strong> – <strong>{fmtDate(board.week_end)}</strong>{' '}
            · {board.clients_count} clientes con adeudo
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar cliente, placa o tel…"
              className="input pl-8 py-1.5 text-sm w-64"
              data-testid="weekly-board-search"
            />
          </div>
          <button
            onClick={onRefresh}
            className="btn-secondary inline-flex items-center gap-1 text-sm"
            data-testid="weekly-board-refresh"
          >
            <ArrowsClockwise size={14} /> Actualizar
          </button>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/payments/weekly-collection/export`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="weekly-board-excel"
          >
            <DownloadSimple size={14} weight="bold" /> Excel
          </a>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="kpi-card">
          <p className="kpi-label">Renta (semana)</p>
          <p className="text-lg font-bold text-[#0F172A]" data-testid="wb-total-expected">{formatCurrency(board.total_week_expected)}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Pagado</p>
          <p className="text-lg font-bold text-emerald-600" data-testid="wb-total-paid">{formatCurrency(board.total_week_paid)}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Falta renta</p>
          <p className="text-lg font-bold text-amber-600" data-testid="wb-total-pending">{formatCurrency(board.total_week_pending)}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Mora</p>
          <p className="text-lg font-bold text-red-600" data-testid="wb-total-penalty">{formatCurrency(board.total_penalty_pending || 0)}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Accesorios</p>
          <p className="text-lg font-bold text-purple-600" data-testid="wb-total-accessory">{formatCurrency(board.total_accessory_pending || 0)}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Arrastres</p>
          <p className="text-lg font-bold text-red-600" data-testid="wb-carryover">{formatCurrency(board.total_carryover_pending)}</p>
        </div>
      </div>

      {/* Table por cliente */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="weekly-board-table">
            <thead>
              <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0] text-left text-xs uppercase tracking-wider text-[#64748B]">
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Teléfono</th>
                <th className="px-4 py-3 text-right">Renta</th>
                <th className="px-4 py-3 text-right">Pagado</th>
                <th className="px-4 py-3 text-right">Saldo</th>
                <th className="px-4 py-3 text-right">Mora</th>
                <th className="px-4 py-3 text-right">Acc.</th>
                <th className="px-4 py-3 text-right">Arrastres</th>
                <th className="px-4 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="9" className="text-center py-8 text-[#94A3B8]">Sin clientes que mostrar</td></tr>
              ) : (
                filtered.map((c) => {
                  const isOpen = !!expanded[c.client_id];
                  return (
                    <React.Fragment key={c.client_id}>
                      <tr
                        className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC] cursor-pointer"
                        onClick={() => toggle(c.client_id)}
                        data-testid={`wb-client-${c.client_id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {isOpen ? <CaretDown size={12} className="text-[#0055FF]" /> : <CaretRight size={12} className="text-[#94A3B8]" />}
                            <span className="font-medium text-[#0F172A]">{c.client_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">
                          {c.phone ? (
                            <a href={`tel:${c.phone}`} className="hover:text-[#0055FF] inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Phone size={11} /> {c.phone}
                            </a>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-[#0F172A]">{formatCurrency(c.week_expected)}</td>
                        <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(c.week_paid)}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${c.week_pending > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {formatCurrency(c.week_pending)}
                        </td>
                        <td className={`px-4 py-3 text-right ${(c.penalty_pending || 0) > 0 ? 'text-red-600 font-semibold' : 'text-[#94A3B8]'}`}>
                          {formatCurrency(c.penalty_pending || 0)}
                        </td>
                        <td className={`px-4 py-3 text-right ${(c.accessory_pending || 0) > 0 ? 'text-purple-700 font-semibold' : 'text-[#94A3B8]'}`}>
                          {formatCurrency(c.accessory_pending || 0)}
                        </td>
                        <td className={`px-4 py-3 text-right ${c.carryover_pending > 0 ? 'text-red-600 font-semibold' : 'text-[#94A3B8]'}`}>
                          {formatCurrency(c.carryover_pending)}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-red-700 bg-red-50/40">
                          {formatCurrency(c.total_pending)}
                        </td>
                      </tr>
                      {isOpen && c.contracts.map((k) => (
                        <tr key={k.contract_id} className="bg-blue-50/30 border-b border-[#F1F5F9]/50 text-xs">
                          <td className="pl-10 pr-4 py-2" colSpan="2">
                            <div className="flex items-center gap-2">
                              <Car size={12} className="text-[#0055FF]" />
                              <span className="font-medium text-[#0F172A]">{k.vehicle_label}</span>
                              <span className="text-[#94A3B8]">{k.vehicle_plates}</span>
                              <span className="ml-auto text-[#64748B]">Renta semanal: {formatCurrency(k.weekly_rent)}</span>
                            </div>
                            {k.week_schedules.length > 0 && (
                              <div className="mt-1 text-[#64748B]">
                                Semana en curso:{' '}
                                {k.week_schedules.map((s) => (
                                  <span key={s.schedule_id} className="mr-2">
                                    Sem {s.week_number} ({fmtDate(s.due_date)}) — falta{' '}
                                    <strong className={s.pending_amount > 0 ? 'text-amber-700' : 'text-emerald-700'}>
                                      {formatCurrency(s.pending_amount)}
                                    </strong>
                                  </span>
                                ))}
                              </div>
                            )}
                            {k.carry_schedules.length > 0 && (
                              <div className="mt-1 text-[#64748B]">
                                Arrastres: {k.carry_schedules.length} cuotas ·{' '}
                                <strong className="text-red-700">
                                  {formatCurrency(k.carry_schedules.reduce((s, x) => s + x.pending_amount, 0))}
                                </strong>
                              </div>
                            )}
                          </td>
                          <td colSpan="7"></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

